import { useState } from 'react';
import { blockCustomer, fetchBlocklist, fetchBlocklistStats, unblockCustomer } from '@/api/blocklist';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { KpiCard } from '@/components/ui/KpiCard';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TextField } from '@/components/ui/TextField';
import { TextArea } from '@/components/ui/TextArea';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatDate, formatNumber, humanizeEnum } from '@/lib/format';
import { errorMessageOf } from '@/lib/errors';
import { cn } from '@/lib/cn';
import type { BlocklistEntry, BlocklistStats } from '@/types';

const AVATAR_TONES = [
  'bg-amber-100 text-amber-700',
  'bg-pink-100 text-pink-700',
  'bg-emerald-100 text-emerald-700',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-blue-100 text-blue-700',
];

function avatarTone(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index)) % AVATAR_TONES.length;
  }
  return AVATAR_TONES[hash];
}

function addedByLabel(entry: BlocklistEntry): string {
  if (entry.isAutoBlocked || entry.isAutoBlock) {
    return 'Auto-Pilot';
  }
  return entry.addedBy ? humanizeEnum(entry.addedBy) : '—';
}

export function BlocklistScreen() {
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));

  const { data: stats, reload: reloadStats } = useAsyncData<BlocklistStats | null>(
    (signal) => fetchBlocklistStats(range, signal),
    [range],
  );
  const { data, errorMessage, isLoading, reload } = useAsyncData<BlocklistEntry[] | null>(
    (signal) => fetchBlocklist(range, signal),
    [range],
  );

  const [isBlockOpen, setIsBlockOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const emailValid = email.includes('@') && email.trim().length > 2;

  function refreshAll() {
    reload();
    reloadStats();
  }

  async function handleBlock() {
    if (!emailValid) {
      return;
    }
    setIsBusy(true);
    try {
      await blockCustomer({
        customerEmail: email.trim(),
        customerPhone: phone.trim() || undefined,
        reason: reason.trim() || undefined,
        customerName: name.trim() || undefined,
      });
      toast.success('Customer blocked');
      setIsBlockOpen(false);
      setEmail('');
      setPhone('');
      setName('');
      setReason('');
      refreshAll();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleUnblock(entry: BlocklistEntry) {
    try {
      await unblockCustomer(entry.id);
      toast.success('Customer unblocked');
      refreshAll();
    } catch (error) {
      toast.error(errorMessageOf(error));
    }
  }

  const entries = data ?? [];

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Customer blocklist"
        description={`${formatNumber(stats?.total ?? entries.length)} customers can't start new returns · checked at portal login`}
        actions={
          <>
            <DateRangePicker value={range} onChange={setRange} />
            <ExportControls type="BLOCKLIST" filenamePrefix="blocklist" range={range} />
            <Button onClick={() => setIsBlockOpen(true)}>Add customer</Button>
          </>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Blocked customers" value={formatNumber(stats?.total)} hint="across all return surfaces" />
        <KpiCard
          label="Auto-blocked"
          value={formatNumber(stats?.autoBlocked)}
          hint="by Auto-Pilot rule"
        />
        <KpiCard label="Manually blocked" value={formatNumber(stats?.manualBlocked)} hint="added by your team" />
      </div>

      {isLoading ? (
        <LoadingState message="Loading blocklist…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[1.8fr_2fr_1fr_1fr_0.6fr] items-center gap-3 border-b border-line bg-surface-sunken/60 px-5 py-3">
                <HeaderCell>Customer</HeaderCell>
                <HeaderCell>Reason</HeaderCell>
                <HeaderCell>Added by</HeaderCell>
                <HeaderCell>Added on</HeaderCell>
                <HeaderCell className="text-right">Returns</HeaderCell>
              </div>

              {entries.length === 0 ? (
                <p className="px-5 py-12 text-center text-sm text-ink-muted">No blocked customers.</p>
              ) : (
                <ul className="divide-y divide-line">
                  {entries.map((entry) => (
                    <li
                      key={entry.id}
                      className="grid grid-cols-[1.8fr_2fr_1fr_1fr_0.6fr] items-center gap-3 px-5 py-3.5"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={cn(
                            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold uppercase',
                            avatarTone(entry.customerEmail),
                          )}
                        >
                          {entry.customerEmail.charAt(0)}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-ink">{entry.customerEmail}</div>
                          {entry.customerPhone ? (
                            <div className="truncate text-xs text-ink-subtle">{entry.customerPhone}</div>
                          ) : null}
                          {entry.customerName ? (
                            <div className="truncate text-xs text-ink-subtle">{entry.customerName}</div>
                          ) : null}
                        </div>
                      </div>

                      <div className="truncate text-sm text-ink-muted" title={entry.reason ?? undefined}>
                        {entry.reason ?? '—'}
                      </div>

                      <div className="truncate text-sm text-ink">{addedByLabel(entry)}</div>

                      <div className="text-sm text-ink-muted">
                        {formatDate(entry.createdAt ?? entry.blockedAt) || '—'}
                      </div>

                      <div className="flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => handleUnblock(entry)}>
                          Unblock
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isBlockOpen}
        onClose={() => setIsBlockOpen(false)}
        title="Add customer to blocklist"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsBlockOpen(false)} disabled={isBusy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleBlock} isLoading={isBusy} disabled={!emailValid}>
              Block customer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <TextField
            label="Customer email"
            type="email"
            placeholder="customer@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            errorText={email && !emailValid ? 'Enter a valid email address' : undefined}
          />
          <TextField
            label="Mobile number (optional)"
            type="tel"
            placeholder="9876543210"
            maxLength={10}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
          <TextField
            label="Customer name (optional)"
            maxLength={55}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextArea label="Reason (optional)" value={reason} maxLength={256} onChange={(event) => setReason(event.target.value)} />
        </div>
      </Modal>
    </div>
  );
}

function HeaderCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-xs font-semibold uppercase tracking-wide text-ink-subtle', className)}>
      {children}
    </span>
  );
}
